// =====================================================================
// gestos.js — Toque: pinça, arrasto, deslize.
//
// PONTO ÚNICO dos gestos. Os dois apps usam este arquivo. Gesto copiado
// em duas telas vira gesto diferente em duas telas — e aí o app "responde
// estranho" numa delas, e ninguém sabe dizer por quê.
//
// ─── O QUE É E O QUE NÃO É ──────────────────────────────────────────
//
// Não existe "versão mobile" separada. Existe o MESMO app, que no celular
// muda de layout e ganha gestos. Duas bases de código divergem na terceira
// semana: a correção é feita numa, esquecida na outra, e o cliente encontra
// o bug justamente no aparelho em que ninguém testou.
//
// Pointer Events (e não touch/mouse separados): o mesmo código atende dedo,
// caneta e mouse. Escrever dois caminhos para a mesma intenção é escrever
// dois bugs.
//
//   abrirFoto()     → visualizador com pinça, arrasto e duplo toque
//   ligarSwipe()    → deslizar para o lado troca de aba
//   ehTouch()       → o aparelho tem dedo? (decide layout, não o CSS sozinho)
// =====================================================================

import { icon } from './icons.js';

export const ehTouch = () =>
  window.matchMedia('(pointer: coarse)').matches || 'ontouchstart' in window;

// =====================================================================
// VISUALIZADOR DE FOTO — pinça para ampliar, arrasto para mover,
// arrastar para baixo para fechar, duplo toque para 2x.
//
// Existe porque a foto no celular é o trabalho, não a ilustração: a
// funcionária amplia o comprovante para ler o valor; o zelador amplia a
// foto da infiltração para ver de onde vem a água. Uma foto que não dá
// zoom, nesse contexto, é uma foto inútil.
// =====================================================================
export function abrirFoto(src, { legenda = '' } = {}) {
  const fundo = document.createElement('div');
  fundo.className = 'visor-foto';
  fundo.innerHTML = `
    <button class="visor-fechar" aria-label="Fechar">${icon('erro')}</button>
    <div class="visor-palco">
      <img src="${src}" alt="${legenda}" draggable="false">
    </div>
    ${legenda ? `<div class="visor-legenda">${legenda}</div>` : ''}
    <div class="visor-dica">Pinça para ampliar · duplo toque · arraste para baixo para fechar</div>`;
  document.body.appendChild(fundo);
  document.body.style.overflow = 'hidden';   // o fundo não rola junto

  const img = fundo.querySelector('img');
  let escala = 1, x = 0, y = 0;
  const LIMITE = 6;

  const aplicar = (transicao = false) => {
    img.style.transition = transicao ? 'transform .18s ease' : 'none';
    img.style.transform = `translate(${x}px, ${y}px) scale(${escala})`;
  };

  const fechar = () => {
    document.body.style.overflow = '';
    fundo.remove();
    document.removeEventListener('keydown', esc);
  };
  const esc = (e) => { if (e.key === 'Escape') fechar(); };
  document.addEventListener('keydown', esc);

  fundo.querySelector('.visor-fechar').onclick = fechar;
  fundo.onclick = (e) => { if (e.target === fundo && escala === 1) fechar(); };

  // ── Ponteiros ativos: 1 = arrastar, 2 = pinça ──────────────────────
  const pontos = new Map();
  let dist0 = 0, escala0 = 1, x0 = 0, y0 = 0, cx0 = 0, cy0 = 0;
  let arrastandoParaFechar = false;

  const centro = () => {
    const ps = [...pontos.values()];
    return {
      x: ps.reduce((s, p) => s + p.x, 0) / ps.length,
      y: ps.reduce((s, p) => s + p.y, 0) / ps.length
    };
  };
  const distancia = () => {
    const [a, b] = [...pontos.values()];
    return Math.hypot(a.x - b.x, a.y - b.y);
  };

  const palco = fundo.querySelector('.visor-palco');

  palco.addEventListener('pointerdown', (e) => {
    palco.setPointerCapture(e.pointerId);
    pontos.set(e.pointerId, { x: e.clientX, y: e.clientY });

    if (pontos.size === 2) {
      dist0 = distancia();
      escala0 = escala;
      const c = centro();
      cx0 = c.x; cy0 = c.y; x0 = x; y0 = y;
    } else {
      x0 = x - e.clientX;
      y0 = y - e.clientY;
      // Sem zoom, o arrasto vertical fecha — o gesto que todo mundo já tem
      // no corpo de fechar foto no celular.
      arrastandoParaFechar = escala === 1;
    }
  });

  palco.addEventListener('pointermove', (e) => {
    if (!pontos.has(e.pointerId)) return;
    pontos.set(e.pointerId, { x: e.clientX, y: e.clientY });

    if (pontos.size >= 2) {
      // Pinça: a escala acompanha a distância entre os dedos, e a imagem
      // se move para manter sob os dedos o ponto que estava lá.
      const d = distancia();
      const c = centro();
      escala = Math.min(LIMITE, Math.max(1, escala0 * (d / dist0)));
      x = x0 + (c.x - cx0);
      y = y0 + (c.y - cy0);
      aplicar();
      e.preventDefault();
      return;
    }

    if (arrastandoParaFechar) {
      y = e.clientY + y0;
      const opacidade = Math.max(0.25, 1 - Math.abs(y) / 420);
      fundo.style.background = `rgba(2, 6, 23, ${0.92 * opacidade})`;
      img.style.transform = `translate(0, ${y}px) scale(${Math.max(0.75, 1 - Math.abs(y) / 900)})`;
      return;
    }

    if (escala > 1) {          // com zoom, arrastar = mover a imagem
      x = e.clientX + x0;
      y = e.clientY + y0;
      aplicar();
    }
  });

  const soltar = (e) => {
    pontos.delete(e.pointerId);

    if (arrastandoParaFechar && Math.abs(y) > 110) return fechar();

    if (arrastandoParaFechar) {   // não foi longe: volta ao lugar
      arrastandoParaFechar = false;
      x = 0; y = 0;
      fundo.style.background = '';
      aplicar(true);
      return;
    }
    if (pontos.size === 0 && escala <= 1.02) {   // encostou no mínimo: centraliza
      escala = 1; x = 0; y = 0;
      aplicar(true);
    }
  };
  palco.addEventListener('pointerup', soltar);
  palco.addEventListener('pointercancel', soltar);

  // Duplo toque / duplo clique: 2x e volta.
  let ultimoToque = 0;
  palco.addEventListener('pointerup', (e) => {
    const agora = Date.now();
    if (agora - ultimoToque < 300) {
      escala = escala > 1 ? 1 : 2.5;
      x = 0; y = 0;
      aplicar(true);
    }
    ultimoToque = agora;
  });

  // Roda do mouse: quem está no desktop também amplia.
  palco.addEventListener('wheel', (e) => {
    e.preventDefault();
    escala = Math.min(LIMITE, Math.max(1, escala * (e.deltaY < 0 ? 1.12 : 0.89)));
    if (escala === 1) { x = 0; y = 0; }
    aplicar();
  }, { passive: false });

  return fechar;
}

/** Toda <img data-zoom> da tela abre no visualizador. Idempotente. */
export function ligarZoomFotos(raiz = document) {
  raiz.querySelectorAll('img[data-zoom]').forEach((im) => {
    if (im.dataset.zoomLigado) return;
    im.dataset.zoomLigado = '1';
    im.style.cursor = 'zoom-in';
    im.addEventListener('click', () =>
      abrirFoto(im.dataset.zoom || im.src, { legenda: im.alt || '' }));
  });
}

// =====================================================================
// DESLIZE ENTRE ABAS
//
// No celular, trocar de aba com o polegar vale mais que qualquer menu.
//
// As três travas que evitam o gesto irritante — aquele que dispara quando
// você só queria rolar a página ou arrastar uma tabela:
//   1. só horizontal (o vertical é da rolagem, e não se disputa isso);
//   2. só se começar fora de um elemento que rola na horizontal;
//   3. exige distância e velocidade mínimas.
// =====================================================================
export function ligarSwipe(el, { aoEsquerda, aoDireita, minimo = 70 } = {}) {
  let x0 = 0, y0 = 0, t0 = 0, valendo = false;

  el.addEventListener('pointerdown', (e) => {
    if (e.pointerType === 'mouse') return;      // mouse não desliza: ele clica
    // Começou dentro de algo que rola de lado (tabela, carrossel)? O gesto
    // é daquele elemento, não da navegação.
    if (e.target.closest('table, .rola-x, .visor-foto, input, textarea, select')) return;
    x0 = e.clientX; y0 = e.clientY; t0 = Date.now(); valendo = true;
  });

  el.addEventListener('pointerup', (e) => {
    if (!valendo) return;
    valendo = false;

    const dx = e.clientX - x0;
    const dy = e.clientY - y0;
    const dt = Date.now() - t0;

    if (Math.abs(dx) < minimo) return;              // curto demais
    if (Math.abs(dx) < Math.abs(dy) * 1.6) return;  // foi mais vertical: era rolagem
    if (dt > 700) return;                           // devagar demais: não foi gesto

    if (dx < 0 && aoEsquerda) aoEsquerda();
    else if (dx > 0 && aoDireita) aoDireita();
  });

  el.addEventListener('pointercancel', () => { valendo = false; });
}
